import React, { useEffect, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useSnack } from '@src/utils/navigatorContext'
import { AppStackParamList, RootStackParamList, ScreenAndProps } from '@src/navigation/types'
import { useGetRequiredPolicyConsents } from '@src/screens/PolicyConsents/hooks/useGetRequiredPolicyConsents'
import { ErrorMessages } from '@src/config/logger'
import { AppointmentConfirmed } from '@src/components/AppointmentConfirmed'
import { InlineAlert } from '@src/components/InlineAlert'
import { UiInlineAlert } from '@src/types'
import { Button } from '@src/components/base/Button'
import { Text } from '@src/components/base'
import { LoadingIndicator } from '@src/components/LoadingIndicator'
import { appointmentKindSelector } from '@src/selectors/app'
import { EhrAppointmentQuery } from '../../graphql/ehrAppointment.generated'
import { insurancePolicySelector } from '../../models/nutritionistHub.selectors'

export const ConfirmationScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const showSnack = useSnack()
  const dispatch = useDispatch()
  const insets = useSafeAreaInsets()

  const route = useRoute<RouteProp<RootStackParamList, 'CallSchedulingConfirmationScreen'>>()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()

  const insurancePolicy = useSelector(insurancePolicySelector)
  const { getRequiredPolicyConsents, loading } = useGetRequiredPolicyConsents()

  const [appointment, setAppointment] = useState<EhrAppointmentQuery['ehrAppointment'] | null>(null)
  const [hasError, setHasError] = useState(false)

  const coachingKind = useSelector(appointmentKindSelector(appointment?.kind))?.coachingKind

  const { appointmentId } = route.params
  const insuranceCheckInProgress = insurancePolicy?.inReview
  const coach = appointment?.providers[0]?.coach
  const coachProp = coach
    ? {
        id: coach.id,
        photo: coach.photo,
        fullName: coach.fullName,
        coachingKind,
      }
    : undefined

  useEffect(() => {
    dispatch({
      type: 'nutritionistHub/fetchInsurancePolicy',
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
      },
    })
    dispatch({
      type: 'nutritionistHub/fetchEhrAppointment',
      payload: { id: appointmentId },
      success: (response: EhrAppointmentQuery['ehrAppointment']) => {
        setAppointment(response)
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
        setHasError(true)
      },
    })
  }, [appointmentId, dispatch, showSnack])

  const onFinish = async () => {
    const nextScreen: ScreenAndProps = {
      screen: 'Drawer',
      params: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
    }
    const requiredPolicies = await getRequiredPolicyConsents()

    if (requiredPolicies.length > 0) {
      navigation.replace('PolicyConsent', { policy: requiredPolicies[0], nextScreen })
    } else {
      navigation.replace(nextScreen.screen, nextScreen.params)
    }
  }

  if (hasError) {
    return (
      <View style={styles.container}>
        <Text type="large" style={styles.informationText}>
          An error occurred while fetching your appointment confirmation details.
        </Text>
      </View>
    )
  }

  if (loading || !appointment) {
    return <LoadingIndicator />
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={[
        styles.containerContent,
        { paddingBottom: Math.max(insets.bottom, 16) },
      ]}
    >
      <View>
        <AppointmentConfirmed
          appointments={[appointment]}
          coaches={coachProp ? [coachProp] : null}
          insurancePolicy={insurancePolicy}
        />
        {insuranceCheckInProgress && (
          <Text type="large" style={styles.informationText}>
            We'll verify your insurance coverage for video calls and send you a confirmation email
            once the benefits check is confirmed. If your insurance doesn't cover the full call
            cost, we won't charge anything without your consent.
          </Text>
        )}
      </View>
      <View style={styles.buttonContainer}>
        <InlineAlert
          category={UiInlineAlert.Neutral}
          message="You can reschedule anytime, or cancel later if you need to."
        />
        <Button
          size="block"
          accessibilityLabel="Continue"
          type="primary"
          disabled={loading}
          onPress={onFinish}
        >
          Continue
        </Button>
      </View>
    </ScrollView>
  )
}

const themedStyles = StyleService.create({
  container: {
    backgroundColor: 'theme.background',
    paddingHorizontal: 20,
    paddingTop: 60,
  },
  containerContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  informationText: {
    textAlign: 'center',
    marginTop: 24,
  },
  buttonContainer: {
    gap: 16,
  },
})
